import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import V3OfferManageContacts from './V3OfferManageContacts';

// Exposes `open()` via ref so the deal-terms-header's "+ Add contacts" link
// can pop the card open before triggering its add-contact handler.
const V3OfferContactsCard = forwardRef(({
  csrfToken,
  team,
  offer,
  currentUser,
  offerContacts,
  offerContactsChanged,
  defaultOpen,
  onRequestAddContact,
}, ref) => {
  const [open, setOpen] = useState(!!defaultOpen);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }), []);

  const count = (offerContacts || []).filter((oc) => oc.id !== undefined).length;
  const hasPending = (offerContacts || []).some((oc) => oc.id === undefined);
  const subtitle = open
    ? 'Add or remove people connected to this offer.'
    : (count === 0
      ? 'No contacts yet · click to add one'
      : `${count} ${count === 1 ? 'contact' : 'contacts'} on this offer · click to view or edit`);

  return (
    <section
      className={`deal-terms-card-v2 ${open ? 'deal-terms-card-v2--open' : ''}`}
      // Override the deal-terms-card-v2 `overflow: hidden` rule so the inline
      // contact-type Popup (rendered absolutely below the Contact Type field)
      // can extend past the bottom border instead of getting clipped.
      style={{ marginTop: '10px', overflow: 'visible' }}
      data-testid="v3-offer-contacts-card"
    >
      <button
        type="button"
        className="deal-terms-card-v2__header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="v3-offer-contacts-card-header"
      >
        <i
          className={`fas ${open ? 'fa-chevron-down' : 'fa-chevron-right'} deal-terms-card-v2__chevron`}
          aria-hidden="true"
        />
        <div className="deal-terms-card-v2__title-block">
          <span className="deal-terms-card-v2__title">Contacts</span>
          <span className="deal-terms-card-v2__subtitle">{subtitle}</span>
        </div>
        <span className="v3-contacts-card-v2__count" data-testid="v3-offer-contacts-card-count">
          <i className="far fa-users" aria-hidden="true" />
          <strong>{count}</strong>
        </span>
      </button>

      {open && (
        <div className="deal-terms-card-v2__body">
          <V3OfferManageContacts
            csrfToken={csrfToken}
            team={team}
            offer={offer}
            currentUser={currentUser}
            offerContacts={offerContacts}
            offerContactsChanged={offerContactsChanged}
            isNewOffer={false}
            embedded
          />
          {!hasPending && currentUser?.can_manage_offer_contacts && onRequestAddContact && (
            <button
              type="button"
              className="v3-contacts-card-v2__add-btn"
              onClick={onRequestAddContact}
              data-testid="v3-offer-contacts-card-add"
            >
              <i className="fas fa-plus-circle" aria-hidden="true" />
              <span>Add contact</span>
            </button>
          )}
        </div>
      )}
    </section>
  );
});

V3OfferContactsCard.displayName = 'V3OfferContactsCard';

V3OfferContactsCard.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  offerContacts: PropTypes.array,
  offerContactsChanged: PropTypes.func.isRequired,
  defaultOpen: PropTypes.bool,
  onRequestAddContact: PropTypes.func,
};

V3OfferContactsCard.defaultProps = {
  offerContacts: [],
  defaultOpen: false,
  onRequestAddContact: null,
};

export default V3OfferContactsCard;
